import React from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Bell, TrendingUp, Eye, AlertCircle, CheckCircle2,
  Lock, ArrowRight, Activity, RefreshCw,
} from 'lucide-react';
import './PerfectCreditMonitoringPage.css';

// SmartCredit affiliate tracking URL (pid=61112 → The Collins Academy)
const PERFECT_MONITORING_AFFILIATE_URL = 'https://www.smartcredit.com/lpg/get-started-trial/?pid=61112';

const FEATURES = [
  {
    icon: Eye,
    title: 'Daily 3-Bureau Monitoring',
    desc: 'We watch Experian, Equifax, and TransUnion every single day for changes to your report.',
  },
  {
    icon: Bell,
    title: 'Instant Alerts',
    desc: 'Get email + SMS the moment a new account, inquiry, or change hits your file.',
  },
  {
    icon: TrendingUp,
    title: 'Score Tracking',
    desc: 'See your VantageScore and FICO move week-by-week with a clean visual timeline.',
  },
  {
    icon: Shield,
    title: 'Dark Web Surveillance',
    desc: 'We scan the dark web 24/7 for your SSN, email, and bank info — alerts the second they show.',
  },
  {
    icon: AlertCircle,
    title: 'Fraud & Identity Protection',
    desc: 'Real-time fraud alerts plus $1M identity theft insurance on every account.',
  },
  {
    icon: RefreshCw,
    title: 'Monthly Strategy Updates',
    desc: 'Collins Academy gives you a monthly playbook of moves to make based on what your report shows.',
  },
];

const INCLUDES = [
  '3-Bureau credit reports updated daily',
  'Real-time FICO + VantageScore tracking',
  'Email + SMS alerts on every change',
  'Hard inquiry monitoring & dispute support',
  'Dark web monitoring (SSN, email, cards)',
  '$1M identity theft insurance',
  'Family plan add-on (up to 4 members)',
  'Cancel anytime — no contracts',
];

const PerfectCreditMonitoringPage = () => {
  const isLive = !!PERFECT_MONITORING_AFFILIATE_URL;

  return (
    <div className="dark-page pcm-page">
      {/* Hero */}
      <section className="pcm-hero">
        <div className="pcm-hero-inner">
          <div className="pcm-badge" data-testid="pcm-badge">
            <Activity size={14} /> 7-DAY TRIAL · JUST $1
          </div>
          <h1 className="display-huge">
            Never Get <span className="text-gradient">Caught Off Guard</span> By Your Credit Again
          </h1>
          <p className="body-large pcm-subtitle">
            Try Perfect Credit Monitoring for <strong>$1</strong> for a full week —
            daily 3-bureau alerts, dark-web surveillance, and a $1M ID theft policy.
            Backed by The Collins Academy's monthly strategy playbook.
          </p>
          <div className="pcm-price-row">
            <div className="pcm-price-tag pcm-price-tag-trial">
              <span className="pcm-price-currency">$</span>
              <span className="pcm-price-num">1</span>
              <span className="pcm-price-per">for 7 days</span>
            </div>
            <div className="pcm-price-perks">
              <div className="pcm-price-perk"><CheckCircle2 size={16} /> Full access during trial</div>
              <div className="pcm-price-perk"><CheckCircle2 size={16} /> Cancel anytime — no contracts</div>
              <div className="pcm-price-perk"><CheckCircle2 size={16} /> $1M ID theft insurance included</div>
            </div>
          </div>
          <p className="pcm-trial-fineprint">
            Then just <strong>$29.99/month</strong> if you love it — cancel any time inside your dashboard.
          </p>
          {isLive ? (
            <a
              href={PERFECT_MONITORING_AFFILIATE_URL}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="btn-primary pcm-cta-btn"
              data-testid="pcm-cta-btn"
            >
              Start My $1 Trial <ArrowRight size={20} />
            </a>
          ) : (
            <button
              type="button"
              className="btn-primary pcm-cta-btn pcm-cta-btn-soon"
              data-testid="pcm-cta-soon"
              disabled
              title="Affiliate setup in progress"
            >
              Launching Soon <Lock size={20} />
            </button>
          )}
          <p className="pcm-trust-line">
            <Shield size={14} /> Bank-grade security · Member-only Collins Academy strategy updates included
          </p>
        </div>
      </section>

      {/* Features grid */}
      <section className="pcm-features">
        <h2 className="display-medium pcm-section-title">What's Included</h2>
        <div className="pcm-features-grid">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="pcm-feature-card">
                <div className="pcm-feature-icon"><Icon size={22} /></div>
                <h3 className="pcm-feature-title">{f.title}</h3>
                <p className="pcm-feature-desc">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Includes list + CTA */}
      <section className="pcm-includes">
        <div className="pcm-includes-card">
          <h3 className="pcm-includes-title">Every Plan Includes</h3>
          <ul className="pcm-includes-list">
            {INCLUDES.map((line, i) => (
              <li key={i}><CheckCircle2 size={16} /> {line}</li>
            ))}
          </ul>
          <div className="pcm-includes-foot">
            <div className="pcm-includes-foot-price">
              <div className="pcm-price-tag pcm-price-tag-sm pcm-price-tag-trial">
                <span className="pcm-price-currency">$</span>
                <span className="pcm-price-num">1</span>
                <span className="pcm-price-per">for 7 days</span>
              </div>
              <span className="pcm-includes-foot-note">then $29.99/mo · cancel anytime</span>
            </div>
            {isLive ? (
              <a
                href={PERFECT_MONITORING_AFFILIATE_URL}
                target="_blank"
                rel="noopener noreferrer sponsored"
                className="btn-primary pcm-cta-btn"
                data-testid="pcm-cta-btn-bottom"
              >
                Start My $1 Trial <ArrowRight size={18} />
              </a>
            ) : (
              <Link to="/contact" className="btn-secondary pcm-cta-soon-link" data-testid="pcm-cta-soon-bottom">
                Get Notified at Launch <ArrowRight size={18} />
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Already have a report? cross-sell */}
      <section className="pcm-cross">
        <div className="pcm-cross-card">
          <h3>Already monitoring? Get your free funding scan first.</h3>
          <p>Upload your latest tri-merge report and we'll tell you exactly how much funding you qualify for — in 60 seconds.</p>
          <Link to="/funding-qualifier" className="btn-secondary" data-testid="pcm-cross-cta">
            Run My Free Credit Scan <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <p className="pcm-disclosure">
        * 7-day trial is $1.00, then $29.99/month thereafter. Cancel anytime from inside your member dashboard before the trial ends to avoid the monthly charge. Perfect Credit Monitoring is provided by our credit monitoring partner. The Collins Academy may earn a commission when you enroll — at no additional cost to you.
      </p>
    </div>
  );
};

export default PerfectCreditMonitoringPage;
